/**
 * `arrangeSignalsClientSide` — QA batch 3 unit G3 (plan D-A step 3), its
 * invariants rewritten by `plans/signals-tab-pr1/fix-plan-batch3.md` D-C
 * (superseding G3's "adjacency defines a run" and G5 D-D's "for overridden
 * levels" — one identity rule, one position rule, one in-leaf order rule).
 * The ONE client-side arrangement of the Signals list: a single stable sort
 * whose comparator walks the group levels in `groupBy` order, then the
 * in-leaf order, then the original index.
 *
 * - **I1 (identity).** A row's group at level *i* is the tuple of its group
 *   LABELS over `groupBy[0..i]` — every level, client levels included —
 *   i.e. exactly the header path FlowTable renders
 *   (`groupValueOf(groupValue(row, key))` for a server level;
 *   `String(clientRank(row, key))` for a client level, which after D-E is
 *   one-to-one with the hour/status label). Two rows share a group iff their
 *   tuples are equal. Adjacency plays no part. Two SFDC-id flows are ONE
 *   `Unresolved Flow` group; `''` and `NULL` Types are ONE `No Type` group.
 * - **I2 (position).** A default-ranked server level orders its groups by
 *   the index of the group's FIRST row in server order (Action View's `Map`
 *   insertion order — `firstIndexRank`); a ranker-overridden level
 *   (`rankerGroupOrder.ts`, via the `serverLevelRank` hook) orders by its
 *   sort key, whose final tiebreak is that same first index. Client levels
 *   (`SIGNAL_SYNTHETIC_GROUP_KEYS` — `local_time`, `business_hours`; never
 *   sent to the server) keep `clientRank`.
 * - **I3 (order inside a leaf).** If the sort is a client sort
 *   (`priority`/`local_time`): by the client value, nulls last, then
 *   original index. Otherwise, if the leaf is **merged** — its rows come
 *   from ≥ 2 adjacency runs of the SERVER-level label tuple — by
 *   `serverSortCompare` (direction-aware, nulls last), then original index.
 *   Otherwise the original index alone: an un-merged leaf's rows are a
 *   subsequence of the server order and keep it byte-for-byte (the client
 *   comparator is never consulted where Postgres already ordered).
 *
 * Why merging is always right (fix-plan-batch3.md §1.3): the server orders
 * by the RAW column, so equal raw values are always adjacent; two
 * non-adjacent runs with one label tuple arise only when the label merges
 * distinct raw values (`resolveFlowName`, the blank rule), and every such
 * merge is intended. Because I1 makes equal ranks at every level mean equal
 * tuples, each group is one contiguous stretch of the output — which is
 * what FlowTable's adjacency-detected headers need.
 *
 * With nothing to do (no levels, server sort) the output is the input.
 * `groupValueOf` and `GROUP_PATH_SEP` are `table-core/grouping.ts`'s (the
 * canonical `FlowTable`/`LedgerTable` versions) — `rankerGroupOrder.ts`
 * imports them from there directly, so the tuple identity cannot drift.
 */
import type { FilterFieldType } from '@shared/flow/filter';
import { SIGNAL_SYNTHETIC_GROUP_KEYS } from '@shared/flow/signal-view';
import { groupValueOf, GROUP_PATH_SEP } from '../table-core/grouping';

/** A table sort — client-applied only when `field` is `priority` or `local_time`. */
export interface ArrangeSort {
  field: string;
  dir: 'ASC' | 'DESC';
}

/** The client-computed values a Signals row may carry (`SignalRowWithComputed` satisfies this). */
export interface ClientSortableSignalRow {
  priority?: number | null;
  local_time?: { hour: number | null; status?: 'open' | 'closed' | 'unknown' } | null;
  [key: string]: unknown;
}

export type GroupValueFn<T> = (row: T, key: string) => unknown;
export type ClientRankFn<T> = (row: T, key: string) => number;

/** Everything a server-level rank function may need, for one level. */
export interface ServerLevelRankContext<T> {
  /** Rows in server order. */
  rows: readonly T[];
  /** The group key at this level. */
  key: string;
  /**
   * Per row (same index as `rows`), the row's full label tuple through this
   * level, NUL-joined — ALL levels, a client level contributing
   * `String(clientRank)` (D-C I1).
   */
  prefixes: readonly string[];
}

/**
 * Per-level rank hook: returns one rank per row (same index as `ctx.rows`).
 * Rows are compared at this level by rank only, and only among rows that
 * already tie on every earlier level — so a rank need only order rows
 * correctly within their parent group.
 */
export type ServerLevelRankFn<T> = (ctx: ServerLevelRankContext<T>) => readonly number[];

export interface ArrangeOptions<T> {
  /** Override the default first-index rank for specific server-level keys. */
  serverLevelRank?: Partial<Record<string, ServerLevelRankFn<T>>>;
  /**
   * The active SERVER sort as a comparator (`buildFieldComparator`),
   * consulted only between two rows of the same MERGED leaf (D-C I3). Absent
   * → a merged leaf keeps original-index order.
   */
  serverSortCompare?: (a: T, b: T) => number;
}

const CLIENT_GROUP_KEYS: ReadonlySet<string> = new Set(SIGNAL_SYNTHETIC_GROUP_KEYS);

/** True for a group level computed in the browser (never sent as `group_by`). */
export function isClientGroupLevel(key: string): boolean {
  return CLIENT_GROUP_KEYS.has(key);
}

const BUSINESS_HOURS_RANK: Record<'open' | 'closed' | 'unknown', number> = { open: 0, closed: 1, unknown: 2 };

/**
 * The client-level ranks (D-A step 3): `local_time` → the contact's local
 * hour 0–23, unknown last (Action View's rule); `business_hours` → open,
 * closed, unknown (D-B's fixed order). Any other key ranks 0.
 */
export function signalClientGroupRank(row: ClientSortableSignalRow, key: string): number {
  if (key === 'local_time') return row.local_time?.hour ?? Infinity;
  if (key === 'business_hours') return BUSINESS_HOURS_RANK[row.local_time?.status ?? 'unknown'];
  return 0;
}

/**
 * Default server-level rank (D-C I2): the index of the FIRST row, in server
 * order, carrying the row's label-tuple prefix — Action View's `Map`
 * insertion order. One rank per distinct prefix, so equal ranks mean one group.
 */
export const firstIndexRank: ServerLevelRankFn<unknown> = ({ prefixes }) => {
  const first = new Map<string, number>();
  return prefixes.map((prefix, i) => {
    const seen = first.get(prefix);
    if (seen !== undefined) return seen;
    first.set(prefix, i);
    return i;
  });
};

function clientSortValue(row: ClientSortableSignalRow, field: 'priority' | 'local_time'): number | null {
  if (field === 'priority') return row.priority ?? null;
  return row.local_time?.hour ?? null;
}

/** Nulls/unknowns always sort last, regardless of direction. */
function compareValues(a: number | null, b: number | null, dir: 'ASC' | 'DESC'): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return dir === 'ASC' ? a - b : b - a;
}

/** One collator for every text comparison — `localeCompare(…, 'en-US')` semantics without a per-call Intl lookup. */
const EN_US_COLLATOR = new Intl.Collator('en-US');

/**
 * A comparable value for `type`, or null for null/undefined/unparseable
 * (NaN) — both sort last. A non-primitive value (never produced by the list
 * endpoint's schema) is treated as no value rather than stringified.
 */
function comparableFieldValue(v: unknown, type: FilterFieldType): number | string | null {
  if (type === 'number') {
    if (typeof v === 'number') return Number.isNaN(v) ? null : v;
    if (typeof v !== 'string' || v.trim() === '') return null; // Number('') is 0, not "no value"
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  if (type === 'date') {
    const t = v instanceof Date ? v.getTime() : typeof v === 'string' ? Date.parse(v) : Number.NaN;
    return Number.isNaN(t) ? null : t;
  }
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return null;
}

/**
 * The server sort (`sort.field`, a SIGNAL_FIELDS key of `type`) as a client
 * comparator — used ONLY inside a merged leaf (D-C I3). `number` numeric,
 * `date` via `Date.parse` (ISO and date-only), every other type through
 * en-US collation (an approximation of Postgres `en_US.UTF-8`, applied only
 * where Postgres could not order the rows itself). Null, undefined and NaN
 * sort last in both directions; DESC negates only the non-null branch.
 */
export function buildFieldComparator<T extends Record<string, unknown>>(
  sort: ArrangeSort,
  type: FilterFieldType,
): (a: T, b: T) => number {
  const sign = sort.dir === 'DESC' ? -1 : 1;
  return (a, b) => {
    const va = comparableFieldValue(a[sort.field], type);
    const vb = comparableFieldValue(b[sort.field], type);
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return va === vb ? 0 : (va < vb ? -1 : 1) * sign;
    return EN_US_COLLATOR.compare(String(va), String(vb)) * sign;
  };
}

export function arrangeSignalsClientSide<T extends ClientSortableSignalRow>(
  rows: T[],
  groupBy: string[],
  sort: ArrangeSort,
  groupValue: GroupValueFn<T>,
  clientRank: ClientRankFn<T>,
  options: ArrangeOptions<T> = {},
): T[] {
  const clientField = sort.field === 'priority' || sort.field === 'local_time' ? sort.field : null;
  if (rows.length === 0) return [];
  if (groupBy.length === 0 && !clientField) return rows;

  // D-C I1: a row's group at level i is its label tuple over groupBy[0..i]
  // — every level, a client level contributing String(clientRank).
  const components: string[][] = rows.map(() => []);
  const serverComponents: string[][] = rows.map(() => []);
  const levelRanks: (readonly number[])[] = [];
  for (const key of groupBy) {
    const client = isClientGroupLevel(key);
    const prefixes = rows.map((row, i) => {
      if (client) {
        components[i].push(String(clientRank(row, key)));
      } else {
        const label = groupValueOf(groupValue(row, key));
        components[i].push(label);
        serverComponents[i].push(label);
      }
      return components[i].join(GROUP_PATH_SEP);
    });
    if (client) {
      levelRanks.push(rows.map(row => clientRank(row, key)));
      continue;
    }
    const rankFn = options.serverLevelRank?.[key] ?? firstIndexRank;
    levelRanks.push(rankFn({ rows, key, prefixes }));
  }

  // D-C I3: a leaf is MERGED when its rows come from >= 2 adjacency runs of
  // the SERVER-level label tuple — only there did the server not order the
  // leaf's rows relative to each other. Only needed under a server sort.
  const leafKeys = components.map(c => c.join(GROUP_PATH_SEP));
  const mergedLeaves = new Set<string>();
  const serverSortCompare = clientField ? undefined : options.serverSortCompare;
  if (serverSortCompare) {
    const runStartsByLeaf = new Map<string, number>();
    let runStart = 0;
    let prevServerTuple: string | null = null;
    rows.forEach((_, i) => {
      const serverTuple = serverComponents[i].join(GROUP_PATH_SEP);
      if (serverTuple !== prevServerTuple) runStart = i;
      prevServerTuple = serverTuple;
      const seenRun = runStartsByLeaf.get(leafKeys[i]);
      if (seenRun === undefined) runStartsByLeaf.set(leafKeys[i], runStart);
      else if (seenRun !== runStart) mergedLeaves.add(leafKeys[i]);
    });
  }

  const order = rows.map((_, i) => i);
  order.sort((a, b) => {
    for (const ranks of levelRanks) {
      const ra = ranks[a];
      const rb = ranks[b];
      // `Infinity - Infinity` is NaN — compare, don't subtract.
      if (ra !== rb) return ra < rb ? -1 : 1;
    }
    if (clientField) {
      const d = compareValues(clientSortValue(rows[a], clientField), clientSortValue(rows[b], clientField), sort.dir);
      if (d !== 0) return d;
    } else if (serverSortCompare && leafKeys[a] === leafKeys[b] && mergedLeaves.has(leafKeys[a])) {
      const d = serverSortCompare(rows[a], rows[b]);
      if (d !== 0) return d;
    }
    return a - b;
  });
  return order.map(i => rows[i]);
}
